import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { User } from "@/lib/types";
import { Json } from "@/integrations/supabase/types";

export interface ContractChange {
  id: string;
  contract_id: string;
  user_id: string;
  changed_at: string;
  change_type: 'create' | 'update' | 'delete';
  field_name?: string;
  old_value?: any;
  new_value?: any;
  metadata?: {
    currencyId?: string;
    [key: string]: any;
  } | Json | null; // Modified to accept Json type from Supabase
  user?: User;
}

// Helper function to format field names to be more human-readable
export function formatFieldName(fieldName?: string): string {
  if (!fieldName) return '';
  
  const fieldMappings: Record<string, string> = {
    'expectedCost': 'Expected Cost',
    'currencyId': 'Currency',
  };

  return fieldMappings[fieldName] || fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

export function useContractChanges() {
  const { toast } = useToast();

  const fetchContractChanges = async (contractId: string): Promise<ContractChange[]> => {
    console.log(`Fetching cost history for contract ${contractId}`);
    
    // First fetch the contract changes
    const { data: changesData, error: changesError } = await supabase
      .from('contract_changes')
      .select('*')
      .eq('contract_id', contractId)
      .or('field_name.eq.expectedCost,change_type.eq.create') // Only get cost changes or contract creation
      .order('changed_at', { ascending: false });

    if (changesError) {
      toast({
        variant: "destructive",
        title: "Error fetching contract changes",
        description: changesError.message
      });
      throw changesError;
    }

    console.log(`Found ${changesData?.length || 0} cost history records`);
    console.log("Contract changes data:", JSON.stringify(changesData, null, 2));

    // Convert changes to our interface format
    const changes: ContractChange[] = (changesData || []).map(item => ({
      id: item.id,
      contract_id: item.contract_id,
      user_id: item.user_id,
      changed_at: item.changed_at,
      change_type: item.change_type as 'create' | 'update' | 'delete',
      field_name: item.field_name,
      old_value: item.old_value,
      new_value: item.new_value,
      metadata: item.metadata
    }));

    // Deduplicate entries by comparing timestamp and change type
    // Group by millisecond timestamp and expected cost field, keeping only the entry with currency info
    const deduplicatedChanges = [];
    const changeMap = new Map();
    
    for (const change of changes) {
      const timestamp = new Date(change.changed_at).getTime();
      const isExpectedCostChange = change.field_name === 'expectedCost' || change.change_type === 'create';
      
      if (!isExpectedCostChange) {
        deduplicatedChanges.push(change); // Keep non-cost changes as is
        continue;
      }
      
      const key = `${timestamp}-${change.change_type}-${change.field_name || 'create'}`;
      const existingChange = changeMap.get(key);
      
      // Determine if this change has currency info
      const hasCurrencyInfo = 
        (change.metadata && typeof change.metadata === 'object' && 'currencyId' in change.metadata) ||
        (change.change_type === 'create' && change.new_value && typeof change.new_value === 'object' && 'currencyId' in change.new_value);
      
      // If we already have an entry for this timestamp, update it only if this one has currency info
      if (existingChange) {
        const existingHasCurrencyInfo = 
          (existingChange.metadata && typeof existingChange.metadata === 'object' && 'currencyId' in existingChange.metadata) ||
          (existingChange.change_type === 'create' && existingChange.new_value && typeof existingChange.new_value === 'object' && 'currencyId' in existingChange.new_value);
        
        // Replace existing entry if this one has currency info and existing doesn't
        if (hasCurrencyInfo && !existingHasCurrencyInfo) {
          changeMap.set(key, change);
        }
      } else {
        // No entry for this timestamp yet, add it
        changeMap.set(key, change);
      }
    }
    
    // Convert the map back to an array
    for (const change of changeMap.values()) {
      deduplicatedChanges.push(change);
    }
    
    // Sort by timestamp, newest first
    deduplicatedChanges.sort((a, b) => 
      new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
    );
    
    console.log(`After deduplication: ${deduplicatedChanges.length} cost history records`);

    // Fetch user data for each change
    if (deduplicatedChanges.length > 0) {
      try {
        // Get a list of unique user IDs
        const userIds = [...new Set(deduplicatedChanges.map(change => change.user_id))];
        
        // For each user ID, try to get user data from auth.users
        for (const userId of userIds) {
          try {
            // Try to get user data directly from auth.users via the Supabase client's auth API
            const { data } = await supabase.auth.getUser();
            
            if (data && data.user && data.user.id === userId) {
              // This works only for the current authenticated user
              const userData = {
                id: data.user.id,
                email: data.user.email || '',
                name: data.user.user_metadata?.name,
              };
              
              // Assign user data to all changes with this user ID
              deduplicatedChanges
                .filter(change => change.user_id === userId)
                .forEach(change => { change.user = userData; });
            } else {
              // For other users, we don't have access to their data
              // We could create a profiles table in the public schema if needed
              // For now, just show the user ID
              deduplicatedChanges
                .filter(change => change.user_id === userId)
                .forEach(change => { 
                  change.user = {
                    id: userId,
                    email: 'User ' + userId.substring(0, 8),
                  };
                });
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
          }
        }
      } catch (error) {
        console.error("Error in user data processing:", error);
      }
    }

    return deduplicatedChanges;
  };

  const useContractChangesQuery = (contractId: string) => useQuery({
    queryKey: ['contract-changes', contractId],
    queryFn: () => fetchContractChanges(contractId),
    enabled: !!contractId,
  });

  return {
    useContractChangesQuery,
    formatFieldName
  };
}
